import React, { useMemo, useRef, useState, useCallback, useEffect } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Drawer,
  FormControl,
  IconButton,
  LinearProgress,
  InputLabel,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import {
  AccessTime,
  AccountTree,
  Add as AddIcon,
  AltRoute,
  Bolt,
  ChevronLeft,
  CenterFocusStrong,
  CheckCircle,
  DataObject,
  DeleteOutline,
  ErrorOutline,
  Http,
  OpenInNew,
  PlayArrow,
  RadioButtonUnchecked,
  Save as SaveIcon,
  CallSplit,
  Code,
  Loop,
  Science,
  Shield,
  Storage,
  Stop,
  ZoomIn,
  ZoomOut
} from '@mui/icons-material';
import { useApi } from '../contexts/ApiContext';
import { usePermissions } from '../hooks/usePermissions';
import ReactFlow, {
  ReactFlowProvider,
  addEdge,
  Background,
  Controls,
  Handle,
  Panel,
  Position,
  useEdgesState,
  useNodesState,
  useReactFlow
} from 'reactflow';

const httpMethods = ['GET', 'POST', 'PUT', 'DELETE', 'PATCH'];

const conditionOperators = [
  { value: 'equals', label: 'Es igual a' },
  { value: 'not_equals', label: 'Es diferente a' },
  { value: 'contains', label: 'Contiene' },
  { value: 'starts_with', label: 'Inicia con' },
  { value: 'ends_with', label: 'Termina con' },
  { value: 'greater_than', label: 'Mayor que' },
  { value: 'less_than', label: 'Menor que' }
];

const browserTargets = [
  { value: 'new_tab', label: 'Nueva pestaña' },
  { value: 'same_tab', label: 'Misma pestaña' },
  { value: 'popup', label: 'Ventana flotante' }
];

const nodeDictionary = {
  trigger: {
    label: 'Trigger inicial',
    accent: '#2e7d32',
    background: 'rgba(76, 175, 80, 0.12)',
    icon: Bolt
  },
  http_request: {
    label: 'HTTP Request',
    accent: '#667eea',
    background: 'rgba(102, 126, 234, 0.12)',
    icon: Http
  },
  open_url: {
    label: 'Abrir URL',
    accent: '#ef6c00',
    background: 'rgba(239, 108, 0, 0.12)',
    icon: OpenInNew
  },
  condition: {
    label: 'Condición',
    accent: '#8e24aa',
    background: 'rgba(142, 36, 170, 0.12)',
    icon: AltRoute
  },
  delay: {
    label: 'Delay',
    accent: '#546e7a',
    background: 'rgba(84, 110, 122, 0.15)',
    icon: AccessTime
  },
  set_variable: {
    label: 'Variable',
    accent: '#6d4c41',
    background: 'rgba(109, 76, 65, 0.12)',
    icon: DataObject
  },
  javascript: {
    label: 'JavaScript',
    accent: '#f9a825',
    background: 'rgba(249, 168, 37, 0.12)',
    icon: Code
  },
  switch: {
    label: 'Switch',
    accent: '#00897b',
    background: 'rgba(0, 137, 123, 0.12)',
    icon: CallSplit
  },
  loop: {
    label: 'Loop',
    accent: '#5c6bc0',
    background: 'rgba(92, 107, 192, 0.12)',
    icon: Loop
  },
  try_catch: {
    label: 'Try / Catch',
    accent: '#d32f2f',
    background: 'rgba(211, 47, 47, 0.12)',
    icon: Shield
  },
  db_query: {
    label: 'Database',
    accent: '#00838f',
    background: 'rgba(0, 131, 143, 0.12)',
    icon: Storage
  },
  default: {
    label: 'Nodo',
    accent: '#616161',
    background: 'rgba(97, 97, 97, 0.10)',
    icon: Bolt
  }
};

const nodePalette = [
  {
    type: 'http_request',
    label: 'HTTP Request',
    description: 'Conecta APIs externas, maneja tokens y payloads dinámicos.'
  },
  {
    type: 'open_url',
    label: 'Abrir URL',
    description: 'Lanza el CRM correcto en el escritorio del agente.'
  },
  {
    type: 'condition',
    label: 'Condición',
    description: 'Evalúa variables del sistema y bifurca tus flujos.'
  },
  {
    type: 'delay',
    label: 'Delay',
    description: 'Controla el ritmo de las peticiones con pausas inteligentes.'
  },
  {
    type: 'set_variable',
    label: 'Set Variable',
    description: 'Guarda resultados intermedios para usarlos después.'
  },
  {
    type: 'javascript',
    label: 'JavaScript',
    description: 'Ejecuta código JS para transformar datos o lógica personalizada.'
  },
  {
    type: 'switch',
    label: 'Switch',
    description: 'Múltiples caminos según el valor de una variable.'
  },
  {
    type: 'loop',
    label: 'Loop',
    description: 'Itera sobre una lista ejecutando nodos por cada elemento.'
  },
  {
    type: 'try_catch',
    label: 'Try / Catch',
    description: 'Captura errores y ejecuta un camino alternativo.'
  },
  {
    type: 'db_query',
    label: 'Database Query',
    description: 'Consulta la base de datos directamente.'
  }
];

const triggerTypes = [
  { value: 'login', label: 'Al iniciar sesión' },
  { value: 'call_start', label: 'Al iniciar llamada' },
  { value: 'call_end', label: 'Al finalizar llamada' },
  { value: 'typification', label: 'Al tipificar' },
  { value: 'skill_change', label: 'Al cambiar de skill' }
];

const systemVariables = [
  'agent',
  'documentagent',
  'agentname',
  'extension_agente',
  'skill',
  'skill_campaign',
  'phone',
  'call_type',
  'call_duration',
  'action_id',
  'data_id',
  'typification_id',
  'typification_name',
  'typification_path',
  'fecha_actual',
  'hora_actual',
  'timestamp',
  'workflow_id',
  'workflow_name',
  'resultado_anterior'
];

const safeParseJSON = (value, fallback) => {
  if (value === null || value === undefined) return fallback;
  if (typeof value === 'object') return value;
  try {
    return JSON.parse(value);
  } catch (error) {
    console.warn('[Workflows] JSON parse error:', error);
    return fallback;
  }
};

const normalizeWorkflow = (workflow) => {
  const triggerParams = safeParseJSON(
    workflow?.trigger_params ?? workflow?.triggerParams,
    {}
  );
  const flowData = safeParseJSON(
    workflow?.flow_data ?? workflow?.flowData,
    { nodes: [], edges: [] }
  );

  return {
    id: workflow.id,
    name: workflow.name,
    description: workflow.description,
    triggerType: workflow.trigger_type ?? workflow.triggerType,
    triggerParams,
    isActive:
      typeof workflow.is_active === 'boolean'
        ? workflow.is_active
        : workflow.isActive ?? false,
    nodes: Array.isArray(flowData.nodes) ? flowData.nodes : [],
    edges: Array.isArray(flowData.edges) ? flowData.edges : [],
    flowData,
    createdAt: workflow.created_at ?? workflow.createdAt,
    updatedAt: workflow.updated_at ?? workflow.updatedAt,
    hasDraftChanges: false
  };
};

const createNodeData = (type, triggerType) => {
  switch (type) {
    case 'trigger':
      return {
        type,
        label: 'Trigger',
        description: triggerTypes.find((trigger) => trigger.value === triggerType)?.label || 'Evento del sistema'
      };
    case 'http_request':
      return {
        type,
        label: 'HTTP Request',
        description: 'Conecta con API externa',
        method: 'POST',
        url: '',
        headers: '{\n  "Content-Type": "application/json"\n}',
        payload: '{\n  "agent": "{agent}"\n}',
        storeAs: 'respuesta_http'
      };
    case 'open_url':
      return {
        type,
        label: 'Abrir URL',
        description: 'Navegador integrado',
        url: '',
        target: 'new_tab',
        notes: ''
      };
    case 'condition':
      return {
        type,
        label: 'Condición',
        description: 'Evalúa datos',
        leftValue: '{skill}',
        operator: 'equals',
        rightValue: '',
        caseSensitive: false
      };
    case 'delay':
      return {
        type,
        label: 'Delay',
        description: 'Pausa planificada',
        seconds: 2
      };
    case 'set_variable':
      return {
        type,
        label: 'Set Variable',
        description: 'Persistencia temporal',
        variableName: 'mi_variable',
        value: '{resultado_anterior}'
      };
    case 'javascript':
      return {
        type,
        label: 'JavaScript',
        description: 'Código personalizado',
        code: '// Variables disponibles en context\n// Ej: context.phone, context.skill\n\nresult = context.phone;'
      };
    case 'switch':
      return {
        type,
        label: 'Switch',
        description: 'Múltiples caminos',
        variable: '{skill}',
        cases: ['valor1', 'valor2']
      };
    case 'loop':
      return {
        type,
        label: 'Loop',
        description: 'Iterar lista',
        source: 'resultado_anterior.data',
        itemVariable: 'current_item',
        indexVariable: 'loop_index'
      };
    case 'try_catch':
      return {
        type,
        label: 'Try / Catch',
        description: 'Manejo de errores'
      };
    case 'db_query':
      return {
        type,
        label: 'Database Query',
        description: 'Consulta SQL',
        query: "SELECT * FROM cti_agent WHERE agent = '{agent}' LIMIT 1"
      };
    default:
      return { type, label: 'Nodo', description: '' };
  }
};
const WorkflowNode = ({ data = {} }) => {
  const definition = nodeDictionary[data.type] || nodeDictionary.default;
  const Icon = definition.icon;

  return (
    <>
      {data.type !== 'trigger' && (
        <Handle
          type="target"
          position={Position.Top}
          style={{
            background: definition.accent,
            border: '2px solid white',
            width: 10,
            height: 10,
            top: -6
          }}
        />
      )}
      <Box
        sx={{
          border: `1px solid ${definition.accent}`,
          backgroundColor: definition.background,
          borderRadius: 2,
          minWidth: 200,
          maxWidth: 260,
          px: 2,
          py: 1.5,
          userSelect: 'none',
          cursor: 'grab',
          textAlign: 'center'
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center" justifyContent="center">
          {Icon && <Icon fontSize="small" sx={{ color: definition.accent }} />}
          <Typography variant="subtitle2" sx={{ color: definition.accent, fontWeight: 600 }}>
            {data.label || definition.label}
          </Typography>
        </Stack>
        {data.description && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
            {data.description}
          </Typography>
        )}
        {data.method && (
          <Chip
            label={data.method}
            size="small"
            sx={{
              mt: 1,
              height: 22,
              fontSize: 11,
              backgroundColor: definition.accent,
              color: '#fff'
            }}
          />
        )}
        {data.url && (
          <Typography
            variant="caption"
            sx={{ mt: 0.5, color: '#616161', display: 'block', wordBreak: 'break-all' }}
          >
            {data.url.length > 40 ? `${data.url.slice(0, 37)}...` : data.url}
          </Typography>
        )}
      </Box>
      {/* Source handles by node type */}
      {data.type === 'condition' && (
        <>
          <Handle id="true" type="source" position={Position.Right}
            style={{ background: '#2e7d32', border: '2px solid white', width: 8, height: 8, right: -6 }} />
          <Handle id="false" type="source" position={Position.Left}
            style={{ background: '#c62828', border: '2px solid white', width: 8, height: 8, left: -6 }} />
        </>
      )}
      {data.type === 'try_catch' && (
        <>
          <Handle id="try" type="source" position={Position.Right}
            style={{ background: '#2e7d32', border: '2px solid white', width: 8, height: 8, right: -6 }} />
          <Handle id="catch" type="source" position={Position.Left}
            style={{ background: '#c62828', border: '2px solid white', width: 8, height: 8, left: -6 }} />
        </>
      )}
      {data.type === 'loop' && (
        <>
          <Handle id="body" type="source" position={Position.Right}
            style={{ background: '#5c6bc0', border: '2px solid white', width: 8, height: 8, right: -6 }} />
          <Handle id="done" type="source" position={Position.Bottom}
            style={{ background: '#2e7d32', border: '2px solid white', width: 10, height: 10, bottom: -6 }} />
        </>
      )}
      {data.type === 'switch' && (
        <>
          {(data.cases || []).map((c, i) => (
            <Handle key={`case_${i}`} id={`case_${i}`} type="source" position={Position.Bottom}
              style={{
                background: definition.accent,
                border: '2px solid white',
                width: 8,
                height: 8,
                bottom: -6,
                left: `${((i + 1) / ((data.cases?.length || 1) + 2)) * 100}%`
              }} />
          ))}
          <Handle id="default" type="source" position={Position.Right}
            style={{ background: '#757575', border: '2px solid white', width: 8, height: 8, right: -6 }} />
        </>
      )}
      {!['condition', 'try_catch', 'loop', 'switch'].includes(data.type) && (
        <Handle type="source" position={Position.Bottom}
          style={{ background: definition.accent, border: '2px solid white', width: 10, height: 10, bottom: -6 }} />
      )}
    </>
  );
};

const nodeTypes = {
  workflowNode: WorkflowNode
};

const CanvasToolbar = ({ onSave }) => {
  const { zoomIn, zoomOut, fitView } = useReactFlow();

  return (
    <Panel position="top-right">
      <Stack direction="row" spacing={1} alignItems="center">
        <Tooltip title="Alejar">
          <IconButton size="small" onClick={() => zoomOut({ duration: 200 })}>
            <ZoomOut fontSize="inherit" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Acercar">
          <IconButton size="small" onClick={() => zoomIn({ duration: 200 })}>
            <ZoomIn fontSize="inherit" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Centrar flujo">
          <IconButton size="small" onClick={() => fitView({ padding: 0.2 })}>
            <CenterFocusStrong fontSize="inherit" />
          </IconButton>
        </Tooltip>
      </Stack>
    </Panel>
  );
};

const NodeConfigPanel = ({ node, updateNodeData, removeNode, systemVars }) => {
  if (!node) {
    return (
      <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
        <Stack spacing={1} alignItems="center" justifyContent="center" sx={{ height: '100%' }}>
          <Bolt color="disabled" sx={{ fontSize: 48 }} />
          <Typography variant="subtitle1">Selecciona un nodo</Typography>
          <Typography variant="body2" color="text.secondary" align="center">
            Haz click en un bloque del flujo para editar su configuración y construir integraciones
            con tu CRM.
          </Typography>
        </Stack>
      </Paper>
    );
  }

  const { data = {} } = node;
  const handleFieldChange = (field) => (event) => {
    const value = event?.target?.value;
    updateNodeData(node.id, { [field]: value });
  };

  const renderTypeFields = () => {
    switch (data.type) {
      case 'http_request':
        return (
          <Stack spacing={2}>
            <FormControl fullWidth>
              <InputLabel>Método</InputLabel>
              <Select label="Método" value={data.method || 'POST'} onChange={handleFieldChange('method')}>
                {httpMethods.map((method) => (
                  <MenuItem key={method} value={method}>
                    {method}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="URL del endpoint"
              placeholder="https://crm.empresa.com/api/login"
              value={data.url || ''}
              onChange={handleFieldChange('url')}
              fullWidth
            />
            <TextField
              label="Headers (JSON)"
              value={data.headers || ''}
              onChange={handleFieldChange('headers')}
              fullWidth
              multiline
              minRows={3}
            />
            <TextField
              label="Payload / Body"
              value={data.payload || ''}
              onChange={handleFieldChange('payload')}
              fullWidth
              multiline
              minRows={4}
            />
            <TextField
              label="Guardar respuesta en variable"
              placeholder="token_crm"
              value={data.storeAs || ''}
              onChange={handleFieldChange('storeAs')}
              fullWidth
            />
          </Stack>
        );
      case 'open_url':
        return (
          <Stack spacing={2}>
            <TextField
              label="URL a abrir"
              placeholder="https://crm.empresa.com/dashboard"
              value={data.url || ''}
              onChange={handleFieldChange('url')}
              fullWidth
            />
            <FormControl fullWidth>
              <InputLabel>Destino</InputLabel>
              <Select label="Destino" value={data.target || 'new_tab'} onChange={handleFieldChange('target')}>
                {browserTargets.map((target) => (
                  <MenuItem key={target.value} value={target.value}>
                    {target.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Notas para el agente"
              placeholder="Se abriró al iniciar sesión"
              value={data.notes || ''}
              onChange={handleFieldChange('notes')}
              fullWidth
              multiline
            />
          </Stack>
        );
      case 'condition':
        return (
          <Stack spacing={2}>
            <TextField
              label="Valor / Variable izquierda"
              placeholder="{skill}"
              value={data.leftValue || ''}
              onChange={handleFieldChange('leftValue')}
              fullWidth
            />
            <FormControl fullWidth>
              <InputLabel>Operador</InputLabel>
              <Select
                label="Operador"
                value={data.operator || 'equals'}
                onChange={handleFieldChange('operator')}
              >
                {conditionOperators.map((operator) => (
                  <MenuItem key={operator.value} value={operator.value}>
                    {operator.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Valor a comparar"
              value={data.rightValue || ''}
              onChange={handleFieldChange('rightValue')}
              fullWidth
            />
            <TextField
              label="Comentario"
              placeholder="Ej: Solo ejecuta para skill Ventas"
              value={data.notes || ''}
              onChange={handleFieldChange('notes')}
              fullWidth
            />
          </Stack>
        );
      case 'delay':
        return (
          <Stack spacing={2}>
            <TextField
              label="Duración en segundos"
              type="number"
              inputProps={{ min: 1 }}
              value={data.seconds || 1}
              onChange={handleFieldChange('seconds')}
              fullWidth
            />
            <TextField
              label="Descripción"
              placeholder="Esperar respuesta del CRM"
              value={data.description || ''}
              onChange={handleFieldChange('description')}
              fullWidth
            />
          </Stack>
        );
      case 'set_variable':
        return (
          <Stack spacing={2}>
            <TextField
              label="Nombre de la variable"
              placeholder="token_crm"
              value={data.variableName || ''}
              onChange={handleFieldChange('variableName')}
              fullWidth
            />
            <TextField
              label="Valor"
              placeholder="{resultado_anterior}"
              value={data.value || ''}
              onChange={handleFieldChange('value')}
              fullWidth
              multiline
            />
          </Stack>
        );
      case 'javascript':
        return (
          <Stack spacing={2}>
            <TextField
              label="Código JavaScript"
              placeholder={'// Variables en context.*\nresult = context.phone;'}
              value={data.code || ''}
              onChange={handleFieldChange('code')}
              fullWidth
              multiline
              minRows={8}
              maxRows={20}
              inputProps={{ style: { fontFamily: 'monospace', fontSize: 13 } }}
            />
            <Alert severity="info" sx={{ fontSize: 12 }}>
              <strong>context</strong> — objeto con todas las variables del sistema (phone, skill, agent, etc.).<br />
              <strong>result</strong> — asigna un valor para usarlo como resultado del nodo.<br />
              <strong>context.mi_variable = valor</strong> — crea nuevas variables accesibles en nodos siguientes.<br />
              Timeout: 5 segundos.
            </Alert>
          </Stack>
        );
      case 'switch':
        return (
          <Stack spacing={2}>
            <TextField
              label="Variable a evaluar"
              placeholder="{skill}"
              value={data.variable || ''}
              onChange={handleFieldChange('variable')}
              fullWidth
            />
            <TextField
              label="Casos (uno por línea)"
              placeholder={'hogarfreemanual1\nventasout1\ncobranzas1'}
              value={(data.cases || []).join('\n')}
              onChange={(e) => {
                const cases = e.target.value.split('\n');
                updateNodeData(node.id, { ...data, cases });
              }}
              fullWidth
              multiline
              minRows={3}
              inputProps={{ style: { fontFamily: 'monospace', fontSize: 13 } }}
            />
            <Alert severity="info" sx={{ fontSize: 12 }}>
              Cada línea es un caso. Las salidas son: <strong>case_0</strong>, <strong>case_1</strong>, etc. (de abajo) y <strong>default</strong> (derecha) si ninguno coincide.
            </Alert>
          </Stack>
        );
      case 'loop':
        return (
          <Stack spacing={2}>
            <TextField
              label="Fuente de datos (variable)"
              placeholder="resultado_anterior.data"
              value={data.source || ''}
              onChange={handleFieldChange('source')}
              fullWidth
            />
            <TextField
              label="Nombre variable del item"
              placeholder="current_item"
              value={data.itemVariable || 'current_item'}
              onChange={handleFieldChange('itemVariable')}
              fullWidth
            />
            <TextField
              label="Nombre variable del índice"
              placeholder="loop_index"
              value={data.indexVariable || 'loop_index'}
              onChange={handleFieldChange('indexVariable')}
              fullWidth
            />
            <Alert severity="info" sx={{ fontSize: 12 }}>
              Salida <strong>body</strong> (derecha): se ejecuta por cada elemento.<br />
              Salida <strong>done</strong> (abajo): continúa después de iterar todo.<br />
              Variables: <code>{'{current_item}'}</code>, <code>{'{loop_index}'}</code>, <code>{'{loop_total}'}</code>.
            </Alert>
          </Stack>
        );
      case 'try_catch':
        return (
          <Stack spacing={2}>
            <Alert severity="info" sx={{ fontSize: 12 }}>
              Salida <strong>try</strong> (derecha, verde): nodos a ejecutar.<br />
              Salida <strong>catch</strong> (izquierda, rojo): se ejecuta si algún nodo del try falla.<br />
              En el catch, <code>{'{error_message}'}</code> contiene el detalle del error.
            </Alert>
          </Stack>
        );
      case 'db_query':
        return (
          <Stack spacing={2}>
            <TextField
              label="Query SQL"
              placeholder={"SELECT * FROM cti_agent\nWHERE agent = '{agent}'\nLIMIT 1"}
              value={data.query || ''}
              onChange={handleFieldChange('query')}
              fullWidth
              multiline
              minRows={5}
              maxRows={15}
              inputProps={{ style: { fontFamily: 'monospace', fontSize: 13 } }}
            />
            <Alert severity="warning" sx={{ fontSize: 12 }}>
              Soporta SELECT, INSERT, UPDATE, DELETE.<br />
              <strong>Prohibido:</strong> DROP, ALTER, TRUNCATE, CREATE.<br />
              Usa variables del sistema: <code>{"{agent}"}</code>, <code>{"{phone}"}</code>, etc.
            </Alert>
          </Stack>
        );
      case 'trigger':
      default:
        return (
          <Alert severity="info">
            Este nodo representa el evento que inicia el flujo. Se crea automáticamente según el trigger
            seleccionado en el workflow.
          </Alert>
        );
    }
  };

  const copyVariable = (variable) => {
    if (typeof navigator !== 'undefined' && navigator.clipboard) {
      navigator.clipboard.writeText(`{${variable}}`);
    }
  };

  return (
    <Paper variant="outlined" sx={{ p: 2, height: '100%', overflow: 'auto' }}>
      <Stack spacing={2}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="subtitle1">
            Configurar {nodeDictionary[data.type]?.label || 'Nodo'}
          </Typography>
          {data.type !== 'trigger' && (
            <Button color="secondary" size="small" onClick={() => removeNode(node.id)} startIcon={<DeleteOutline />}>
              Eliminar
            </Button>
          )}
        </Stack>
        <TextField
          label="Título"
          value={data.label || ''}
          onChange={handleFieldChange('label')}
          fullWidth
        />
        <TextField
          label="Descripción"
          value={data.description || ''}
          onChange={handleFieldChange('description')}
          fullWidth
          multiline
          minRows={2}
        />
        {renderTypeFields()}
        <Divider />
        <Typography variant="caption" color="text.secondary">
          Variables del sistema (click para copiar):
        </Typography>
        <Stack direction="row" spacing={1} flexWrap="wrap">
          {systemVars.map((variable) => (
            <Chip
              key={variable}
              label={`{${variable}}`}
              size="small"
              onClick={() => copyVariable(variable)}
              sx={{ cursor: 'pointer' }}
            />
          ))}
        </Stack>
      </Stack>
    </Paper>
  );
};
const WorkflowsPage = () => {
  const { workflows: workflowsApi } = useApi();
  const { canAction } = usePermissions();
  const [workflows, setWorkflows] = useState([]);
  const [selectedWorkflowId, setSelectedWorkflowId] = useState(null);
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [activeNodeId, setActiveNodeId] = useState(null);
  const [suspendSync, setSuspendSync] = useState(false);
  const suspendSyncRef = useRef(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [workflowsDrawerOpen, setWorkflowsDrawerOpen] = useState(false);
  const [blocksDrawerOpen, setBlocksDrawerOpen] = useState(false);
  const [configDrawerOpen, setConfigDrawerOpen] = useState(false);
  const [loadingWorkflows, setLoadingWorkflows] = useState(false);
  const [creatingWorkflow, setCreatingWorkflow] = useState(false);
  const [savingWorkflow, setSavingWorkflow] = useState(false);
  const [testingWorkflow, setTestingWorkflow] = useState(false);
  const [testDialogOpen, setTestDialogOpen] = useState(false);
  const [testAgentId, setTestAgentId] = useState('');
  const [testPayload, setTestPayload] = useState('{\n  "numero_cliente": "3001234567"\n}');
  const [testResult, setTestResult] = useState(null);
  const [newWorkflow, setNewWorkflow] = useState({
    name: '',
    description: '',
    triggerType: ''
  });
  const [feedback, setFeedback] = useState({ open: false, message: '', severity: 'success' });

  const selectedWorkflow = useMemo(
    () => workflows.find((workflow) => workflow.id === selectedWorkflowId) || null,
    [workflows, selectedWorkflowId]
  );

  const activeNode = useMemo(
    () => nodes.find((node) => node.id === activeNodeId) || null,
    [nodes, activeNodeId]
  );

  const showFeedback = useCallback((message, severity = 'success') => {
    setFeedback({ open: true, message, severity });
  }, []);

  const createTriggerNode = useCallback(
    (triggerType) => ({
      id: `trigger_${Date.now()}`,
      type: 'workflowNode',
      position: { x: 80, y: 60 },
      data: createNodeData('trigger', triggerType)
    }),
    []
  );

  const fetchWorkflows = useCallback(async () => {
    setLoadingWorkflows(true);
    try {
      const response = await workflowsApi.list({ limit: 100 });
      const fetched = (response.workflows || []).map(normalizeWorkflow);
      setWorkflows(fetched);

      if (selectedWorkflowId) {
        const selected = fetched.find((workflow) => workflow.id === selectedWorkflowId);
        if (selected) {
          suspendSyncRef.current = true; setTimeout(() => { suspendSyncRef.current = false; }, 500);
          setNodes(selected.nodes || []);
          setEdges(selected.edges || []);
        } else {
          setSelectedWorkflowId(null);
          setNodes([]);
          setEdges([]);
          setActiveNodeId(null);
        }
      }
    } catch (error) {
      showFeedback(error.message || 'No se pudieron cargar los workflows.', 'error');
    } finally {
      setLoadingWorkflows(false);
    }
  }, [workflowsApi, selectedWorkflowId, showFeedback]);

  useEffect(() => {
    fetchWorkflows();
  }, [fetchWorkflows]);

  const handleAddNode = (type) => {
    if (!selectedWorkflow) {
      showFeedback('Selecciona un workflow antes de agregar bloques.', 'warning');
      return;
    }

    const id = `${type}_${Date.now()}`;
    const nextNode = {
      id,
      type: 'workflowNode',
      position: {
        x: 140 + nodes.length * 35,
        y: 180 + (nodes.length % 5) * 70
      },
      data: createNodeData(type)
    };

    setNodes((current) => current.concat(nextNode));
    setActiveNodeId(id);
  };

  const persistDraft = (payload = {}) => {
    if (!selectedWorkflow) return;

    setWorkflows((current) =>
      current.map((workflow) => {
        if (workflow.id !== selectedWorkflow.id) return workflow;
        return {
          ...workflow,
          ...payload,
          hasDraftChanges:
            payload.hasDraftChanges !== undefined ? payload.hasDraftChanges : true
        };
      })
    );
  };

  const handleCreateWorkflow = async () => {
    if (!newWorkflow.name || !newWorkflow.triggerType) return;

    setCreatingWorkflow(true);
    try {
      const triggerNode = createTriggerNode(newWorkflow.triggerType);
      const payload = {
        name: newWorkflow.name,
        description: newWorkflow.description,
        triggerType: newWorkflow.triggerType,
        triggerParams: {},
        flowData: { nodes: [triggerNode], edges: [] },
        isActive: false
      };

      const response = await workflowsApi.create(payload);
      const created = normalizeWorkflow(response.workflow);

      setWorkflows((current) => [created, ...current]);
      setSelectedWorkflowId(created.id);
      suspendSyncRef.current = true; setTimeout(() => { suspendSyncRef.current = false; }, 500);
      setNodes(created.nodes);
      setEdges(created.edges);
      setActiveNodeId(created.nodes?.[0]?.id || null);
      setNewWorkflow({ name: '', description: '', triggerType: '' });
      setOpenDialog(false);
      showFeedback('Workflow creado. Agrega nodos para continuar.');
    } catch (error) {
      showFeedback(error.message || 'No se pudo crear el workflow.', 'error');
    } finally {
      setCreatingWorkflow(false);
    }
  };

  const handleSelectWorkflow = (workflow) => {
    setSelectedWorkflowId(workflow.id);
    suspendSyncRef.current = true; setTimeout(() => { suspendSyncRef.current = false; }, 500);
    setNodes(workflow.nodes || []);
    setEdges(workflow.edges || []);
    setActiveNodeId(null);
    setWorkflowsDrawerOpen(false);
  };

  const handleSaveWorkflow = async () => {
    if (!selectedWorkflow) return;

    setSavingWorkflow(true);
    try {
      const response = await workflowsApi.update(selectedWorkflow.id, {
        name: selectedWorkflow.name,
        description: selectedWorkflow.description,
        triggerType: selectedWorkflow.triggerType,
        triggerParams: selectedWorkflow.triggerParams || {},
        flowData: { nodes, edges },
        isActive: selectedWorkflow.isActive
      });

      const updated = normalizeWorkflow(response.workflow);
      setWorkflows((current) =>
        current.map((workflow) => (workflow.id === updated.id ? updated : workflow))
      );
      suspendSyncRef.current = true;
      setNodes(updated.nodes);
      setEdges(updated.edges);
      setActiveNodeId(null);
      setTimeout(() => { suspendSyncRef.current = false; }, 500);
      showFeedback('Workflow guardado correctamente.');
    } catch (error) {
      showFeedback(error.message || 'No se pudo guardar el workflow.', 'error');
    } finally {
      setSavingWorkflow(false);
    }
  };

  const handleToggleActive = async (workflow) => {
    try {
      const response = await workflowsApi.update(workflow.id, {
        isActive: !workflow.isActive
      });
      const updated = normalizeWorkflow(response.workflow);

      setWorkflows((current) =>
        current.map((item) => {
          if (item.id !== updated.id) return item;
          if (updated.id === selectedWorkflowId && selectedWorkflow?.hasDraftChanges) {
            return {
              ...item,
              isActive: updated.isActive,
              updatedAt: updated.updatedAt
            };
          }
          return updated;
        })
      );

      if (selectedWorkflowId === workflow.id && !selectedWorkflow?.hasDraftChanges) {
        suspendSyncRef.current = true; setTimeout(() => { suspendSyncRef.current = false; }, 500);
        setNodes(updated.nodes);
        setEdges(updated.edges);
      }

      showFeedback(
        workflow.isActive ? 'Workflow pausado.' : 'Workflow activado.',
        workflow.isActive ? 'info' : 'success'
      );
    } catch (error) {
      showFeedback(error.message || 'No se pudo actualizar el estado.', 'error');
    }
  };

  const handleRunTest = async () => {
    if (!selectedWorkflow) return;
    if (!testAgentId.trim()) {
      showFeedback('Ingresa el ID del agente para ejecutar la prueba.', 'warning');
      return;
    }

    let parsedPayload = {};
    if (testPayload.trim()) {
      try {
        parsedPayload = JSON.parse(testPayload);
      } catch (error) {
        showFeedback('El payload de prueba no es un JSON válido.', 'error');
        return;
      }
    }

    setTestingWorkflow(true);
    setTestResult(null);
    try {
      const response = await workflowsApi.test(selectedWorkflow.id, {
        agentId: testAgentId.trim(),
        testData: parsedPayload
      });
      setTestResult({
        type: 'success',
        message: response.message || 'Prueba ejecutada.',
        execution: response.execution || null
      });
      showFeedback('Prueba ejecutada correctamente.', 'success');
    } catch (error) {
      const message = error.message || 'No se pudo ejecutar la prueba.';
      setTestResult({ type: 'error', message });
      showFeedback(message, 'error');
    } finally {
      setTestingWorkflow(false);
    }
  };

  const handleDeleteWorkflow = async (workflowId) => {
    try {
      await workflowsApi.remove(workflowId);
      setWorkflows((current) => current.filter((workflow) => workflow.id !== workflowId));
      if (selectedWorkflowId === workflowId) {
        setSelectedWorkflowId(null);
        setNodes([]);
        setEdges([]);
        setActiveNodeId(null);
      }
      showFeedback('Workflow eliminado.', 'info');
    } catch (error) {
      showFeedback(error.message || 'No se pudo eliminar el workflow.', 'error');
    }
  };

  const handleDeleteNode = (nodeId) => {
    setNodes((current) => current.filter((node) => node.id !== nodeId));
    setEdges((current) =>
      current.filter((edge) => edge.source !== nodeId && edge.target !== nodeId)
    );
    if (activeNodeId === nodeId) {
      setActiveNodeId(null);
    }
  };

  const updateNodeData = (nodeId, newData) => {
    setNodes((current) =>
      current.map((node) =>
        node.id === nodeId
          ? {
              ...node,
              data: {
                ...node.data,
                ...newData
              }
            }
          : node
      )
    );
  };

  const onConnect = useCallback(
    (params) =>
      setEdges((current) =>
        addEdge(
          {
            ...params,
            style: { strokeWidth: 2 },
            markerEnd: { type: 'arrowclosed' }
          },
          current
        )
      ),
    [setEdges]
  );

  useEffect(() => {
    if (!selectedWorkflow) return;
    if (suspendSyncRef.current) return;
    persistDraft({ nodes, edges, flowData: { nodes, edges } });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nodes, edges, selectedWorkflow]);

  return (
    <ReactFlowProvider>
      <Stack sx={{ height: 'calc(100vh - 80px)', overflow: 'hidden' }} spacing={0}>
        {loadingWorkflows && (
          <LinearProgress sx={{ borderRadius: 1 }} />
        )}

        <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>
          {selectedWorkflow ? (
            <Stack spacing={1} sx={{ flex: 1, overflow: 'hidden' }}>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                justifyContent="space-between"
                alignItems="center"
                gap={1}
                flexWrap="wrap"
              >
                <Stack direction="row" spacing={1} alignItems="center">
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<AccountTree />}
                    onClick={() => setWorkflowsDrawerOpen(true)}
                  >
                    Workflows
                  </Button>
                  <Chip
                    label={loadingWorkflows ? '...' : `${workflows.length}`}
                    size="small"
                    color="primary"
                  />
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() => setOpenDialog(true)}
                  >
                    Nuevo
                  </Button>
                  <Divider orientation="vertical" flexItem />
                  <Typography variant="subtitle2" noWrap>
                    {selectedWorkflow.name}
                  </Typography>
                  <Chip
                    label={triggerTypes.find((t) => t.value === selectedWorkflow.triggerType)?.label}
                    size="small"
                    variant="outlined"
                    icon={<Bolt sx={{ fontSize: 14 }} />}
                  />
                  {selectedWorkflow.hasDraftChanges ? (
                    <Chip label="Sin guardar" size="small" color="warning" variant="outlined" icon={<ErrorOutline sx={{ fontSize: 14 }} />} />
                  ) : (
                    <Chip label="Guardado" size="small" color="success" variant="outlined" icon={<CheckCircle sx={{ fontSize: 14 }} />} />
                  )}
                </Stack>
                <Stack direction="row" spacing={1}>
                  {canAction('test_workflow') && <Button
                    variant="outlined"
                    size="small"
                    startIcon={<Science />}
                    onClick={() => {
                      setTestDialogOpen(true);
                      setTestResult(null);
                    }}
                  >
                    Probar
                  </Button>}
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<SaveIcon />}
                    onClick={handleSaveWorkflow}
                    disabled={savingWorkflow}
                  >
                    {savingWorkflow ? 'Guardando...' : 'Guardar'}
                  </Button>
                </Stack>
              </Stack>

              <Divider />

              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: '1fr',
                    lg: '240px 1fr 300px'
                  },
                  gap: 1,
                  flex: 1,
                  overflow: 'hidden'
                }}
              >
                {/* Panel izquierdo: visible en lg+, drawer en < lg */}
                <Box sx={{ display: { xs: 'none', lg: 'block' }, overflow: 'hidden' }}>
                  <Paper variant="outlined" sx={{ p: 1.5, height: '100%', overflow: 'auto' }}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      Bloques disponibles
                    </Typography>
                    <List dense>
                      {nodePalette.map((node) => {
                        const Icon = nodeDictionary[node.type]?.icon;
                        return (
                          <ListItemButton
                            key={node.type}
                            onClick={() => handleAddNode(node.type)}
                            sx={{ borderRadius: 1, mb: 0.5 }}
                          >
                            <ListItemIcon>
                              {Icon && (
                                <Icon fontSize="small" sx={{ color: nodeDictionary[node.type].accent }} />
                              )}
                            </ListItemIcon>
                            <ListItemText
                              primary={node.label}
                              secondary={node.description}
                              primaryTypographyProps={{ fontSize: 14, fontWeight: 600 }}
                              secondaryTypographyProps={{ fontSize: 12 }}
                            />
                          </ListItemButton>
                        );
                      })}
                    </List>
                  </Paper>
                </Box>

                {/* Canvas central */}
                <Box sx={{ overflow: 'hidden', position: 'relative' }}>
                  <Paper
                    variant="outlined"
                    sx={{
                      height: '100%',
                      borderRadius: 2,
                      overflow: 'hidden',
                      position: 'relative'
                    }}
                  >
                    <ReactFlow
                      nodes={nodes}
                      edges={edges}
                      onNodesChange={onNodesChange}
                      onEdgesChange={onEdgesChange}
                      onConnect={onConnect}
                      nodeTypes={nodeTypes}
                      fitView
                      proOptions={{ hideAttribution: true }}
                      snapToGrid
                      snapGrid={[20, 20]}
                      onNodeClick={(_, node) => {
                        setActiveNodeId(node.id);
                        setConfigDrawerOpen(true);
                      }}
                      onPaneClick={() => setActiveNodeId(null)}
                      elevateNodesOnSelect
                    >
                      <Background variant="dots" gap={16} size={1} />
                      <Controls position="bottom-left" showInteractive={false} />
                      <CanvasToolbar onSave={handleSaveWorkflow} />
                    </ReactFlow>
                    {/* Botones flotantes para pantallas < lg */}
                    <Stack
                      direction="row"
                      spacing={1}
                      sx={{
                        display: { xs: 'flex', lg: 'none' },
                        position: 'absolute',
                        top: 8,
                        left: 8,
                        zIndex: 5
                      }}
                    >
                      <Button
                        size="small"
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => setBlocksDrawerOpen(true)}
                        sx={{ textTransform: 'none', fontSize: 12 }}
                      >
                        Bloques
                      </Button>
                      {activeNode && (
                        <Button
                          size="small"
                          variant="contained"
                          color="secondary"
                          startIcon={<DataObject />}
                          onClick={() => setConfigDrawerOpen(true)}
                          sx={{ textTransform: 'none', fontSize: 12 }}
                        >
                          Configurar
                        </Button>
                      )}
                    </Stack>
                  </Paper>
                </Box>

                {/* Panel derecho: visible en lg+, drawer en < lg */}
                <Box sx={{ display: { xs: 'none', lg: 'block' }, overflow: 'auto' }}>
                  <NodeConfigPanel
                    node={activeNode}
                    updateNodeData={updateNodeData}
                    removeNode={handleDeleteNode}
                    systemVars={systemVariables}
                  />
                </Box>
              </Box>

              {/* Drawer de bloques para pantallas < lg */}
              <Drawer
                anchor="left"
                open={blocksDrawerOpen}
                onClose={() => setBlocksDrawerOpen(false)}
                sx={{ display: { lg: 'none' } }}
                PaperProps={{ sx: { width: { xs: '80%', sm: 300 }, p: 2 } }}
              >
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                  <Typography variant="subtitle1" fontWeight={600}>Bloques disponibles</Typography>
                  <IconButton size="small" onClick={() => setBlocksDrawerOpen(false)}>
                    <ChevronLeft />
                  </IconButton>
                </Stack>
                <List dense>
                  {nodePalette.map((node) => {
                    const Icon = nodeDictionary[node.type]?.icon;
                    return (
                      <ListItemButton
                        key={node.type}
                        onClick={() => { handleAddNode(node.type); setBlocksDrawerOpen(false); }}
                        sx={{ borderRadius: 1, mb: 0.5 }}
                      >
                        <ListItemIcon>
                          {Icon && <Icon fontSize="small" sx={{ color: nodeDictionary[node.type].accent }} />}
                        </ListItemIcon>
                        <ListItemText
                          primary={node.label}
                          secondary={node.description}
                          primaryTypographyProps={{ fontSize: 14, fontWeight: 600 }}
                          secondaryTypographyProps={{ fontSize: 12 }}
                        />
                      </ListItemButton>
                    );
                  })}
                </List>
              </Drawer>

              {/* Drawer de configuración para pantallas < lg */}
              <Drawer
                anchor="right"
                open={configDrawerOpen && !!activeNode}
                onClose={() => setConfigDrawerOpen(false)}
                sx={{ display: { lg: 'none' } }}
                PaperProps={{ sx: { width: { xs: '90%', sm: 360 }, p: 2 } }}
              >
                <NodeConfigPanel
                  node={activeNode}
                  updateNodeData={updateNodeData}
                  removeNode={handleDeleteNode}
                  systemVars={systemVariables}
                />
              </Drawer>
            </Stack>
          ) : (
            <Stack spacing={3} alignItems="center" justifyContent="center" sx={{ height: '100%', py: 6 }}>
              <Bolt color="disabled" sx={{ fontSize: 56 }} />
              <Typography variant="h6">Selecciona o crea un workflow</Typography>
              <Stack direction="row" spacing={1}>
                <Button
                  variant="outlined"
                  startIcon={<AccountTree />}
                  onClick={() => setWorkflowsDrawerOpen(true)}
                >
                  Workflows configurados
                </Button>
                <Chip
                  label={loadingWorkflows ? '...' : `${workflows.length}`}
                  size="small"
                  color="primary"
                  sx={{ alignSelf: 'center' }}
                />
                {canAction('create_workflow') && <Button variant="contained" startIcon={<AddIcon />} onClick={() => setOpenDialog(true)}>
                  Nuevo Workflow
                </Button>}
              </Stack>
            </Stack>
          )}
        </Paper>

      </Stack>

      <Drawer
        anchor="left"
        open={workflowsDrawerOpen}
        onClose={() => setWorkflowsDrawerOpen(false)}
        PaperProps={{
          sx: {
            width: { xs: '100%', sm: 360 },
            p: 2
          }
        }}
      >
        <Stack spacing={2} sx={{ height: '100%' }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Box>
              <Typography variant="h6">Workflows configurados</Typography>
              <Typography variant="body2" color="text.secondary">
                {workflows.length === 0
                  ? 'Comienza creando tu primer flujo.'
                  : 'Selecciona un flujo para editarlo.'}
              </Typography>
            </Box>
            <IconButton size="small" onClick={() => setWorkflowsDrawerOpen(false)}>
              <ChevronLeft />
            </IconButton>
          </Stack>

          {loadingWorkflows && <LinearProgress sx={{ mb: 2 }} />}

          {workflows.length === 0 && (
            <Card variant="outlined">
              <CardContent>
                <Typography variant="subtitle2">Aún no hay workflows</Typography>
                <Typography variant="body2" color="text.secondary">
                  Crea flujos para login, llamadas o procesos especiales.
                </Typography>
              </CardContent>
            </Card>
          )}

          {workflows.length > 0 && (
            <List sx={{ mt: 1 }}>
              {workflows.map((workflow) => (
                <ListItemButton
                  key={workflow.id}
                  selected={selectedWorkflow?.id === workflow.id}
                  onClick={() => handleSelectWorkflow(workflow)}
                  sx={{ borderRadius: 2, mb: 1 }}
                >
                  <ListItemIcon>
                    {workflow.isActive ? (
                      <CheckCircle fontSize="small" color="success" />
                    ) : (
                      <RadioButtonUnchecked fontSize="small" color="disabled" />
                    )}
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Stack direction="row" gap={1} alignItems="center">
                        <Typography variant="subtitle1">{workflow.name}</Typography>
                        {workflow.hasDraftChanges && (
                          <Chip label="Borrador" size="small" color="warning" variant="outlined" />
                        )}
                      </Stack>
                    }
                    secondary={
                      <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
                        <Chip
                          size="small"
                          label={triggerTypes.find((t) => t.value === workflow.triggerType)?.label}
                          color="info"
                        />
                        <Typography variant="caption" color="text.secondary">
                          {workflow.nodes?.length || 0} nodos · {workflow.edges?.length || 0} conexiones
                        </Typography>
                      </Stack>
                    }
                  />
                  <Tooltip title={workflow.isActive ? 'Pausar' : 'Activar'}>
                    <IconButton
                      size="small"
                      onClick={(event) => {
                        event.stopPropagation();
                        handleToggleActive(workflow);
                      }}
                    >
                      {workflow.isActive ? (
                        <Stop fontSize="inherit" color="error" />
                      ) : (
                        <PlayArrow fontSize="inherit" color="success" />
                      )}
                    </IconButton>
                  </Tooltip>
                  {canAction('delete_workflow') && <Tooltip title="Eliminar">
                    <IconButton
                      size="small"
                      onClick={(event) => {
                        event.stopPropagation();
                        handleDeleteWorkflow(workflow.id);
                      }}
                    >
                      <DeleteOutline fontSize="inherit" />
                    </IconButton>
                  </Tooltip>}
                </ListItemButton>
              ))}
            </List>
          )}
        </Stack>
      </Drawer>

      <Dialog open={testDialogOpen} onClose={() => setTestDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Probar workflow</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Alert severity="info">
              Ejecuta el flujo contra el motor de workflows usando un agente específico. Las acciones configuradas (HTTP, abrir URL, etc.) se dispararán como si el evento real ocurriera.
            </Alert>
            <TextField
              label="ID del agente"
              value={testAgentId}
              onChange={(event) => setTestAgentId(event.target.value)}
              fullWidth
              required
            />
            <TextField
              label="Payload de prueba (JSON opcional)"
              value={testPayload}
              onChange={(event) => setTestPayload(event.target.value)}
              fullWidth
              multiline
              minRows={4}
              helperText="Incluye datos adicionales para la ejecución (ej. número de cliente, skill, etc.)"
            />
            {testResult && (
              <Box>
                <Alert severity={testResult.type === 'error' ? 'error' : 'success'} sx={{ mb: 1 }}>
                  {testResult.message}
                </Alert>
                {testResult.execution && (
                  <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50', maxHeight: 300, overflow: 'auto' }}>
                    <Typography variant="subtitle2" gutterBottom>Resultado de ejecución</Typography>
                    <Stack spacing={1}>
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                        <Typography variant="body2" color="text.secondary">Estado:</Typography>
                        <Chip
                          size="small"
                          label={testResult.execution.execution_status}
                          color={testResult.execution.execution_status === 'completed' ? 'success' : 'error'}
                        />
                      </Box>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Typography variant="body2" color="text.secondary">Pasos ejecutados:</Typography>
                        <Typography variant="body2">{testResult.execution.current_step || 0}</Typography>
                      </Box>
                      {testResult.execution.error_message && (
                        <Alert severity="error" variant="outlined" sx={{ py: 0 }}>
                          {testResult.execution.error_message}
                        </Alert>
                      )}
                      {testResult.execution.step_results && Object.keys(testResult.execution.step_results).length > 0 && (
                        <Box>
                          <Typography variant="body2" color="text.secondary" gutterBottom>Detalle por paso:</Typography>
                          <Box
                            component="pre"
                            sx={{
                              p: 1.5,
                              bgcolor: 'grey.900',
                              color: 'grey.100',
                              borderRadius: 1,
                              fontSize: 12,
                              overflow: 'auto',
                              maxHeight: 160,
                              m: 0
                            }}
                          >
                            {JSON.stringify(testResult.execution.step_results, null, 2)}
                          </Box>
                        </Box>
                      )}
                    </Stack>
                  </Paper>
                )}
              </Box>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTestDialogOpen(false)}>Cerrar</Button>
          <Button onClick={handleRunTest} variant="contained" disabled={testingWorkflow}>
            {testingWorkflow ? 'Ejecutando...' : 'Ejecutar prueba'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Nuevo Workflow</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              autoFocus
              label="Nombre"
              placeholder="Ej: Login CRM Ventas"
              value={newWorkflow.name}
              onChange={(event) => setNewWorkflow((current) => ({ ...current, name: event.target.value }))}
              fullWidth
            />
            <TextField
              label="Descripción"
              placeholder="Describe qué automatiza este flujo"
              value={newWorkflow.description}
              onChange={(event) =>
                setNewWorkflow((current) => ({ ...current, description: event.target.value }))
              }
              fullWidth
              multiline
              rows={3}
            />
            <FormControl fullWidth>
              <InputLabel>Trigger inicial</InputLabel>
              <Select
                label="Trigger inicial"
                value={newWorkflow.triggerType}
                onChange={(event) =>
                  setNewWorkflow((current) => ({ ...current, triggerType: event.target.value }))
                }
              >
                {triggerTypes.map((trigger) => (
                  <MenuItem key={trigger.value} value={trigger.value}>
                    {trigger.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancelar</Button>
          <Button
            onClick={handleCreateWorkflow}
            variant="contained"
            disabled={!newWorkflow.name || !newWorkflow.triggerType || creatingWorkflow}
          >
            {creatingWorkflow ? 'Creando...' : 'Crear workflow'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={feedback.open}
        autoHideDuration={4000}
        onClose={() => setFeedback((current) => ({ ...current, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          severity={feedback.severity}
          variant="filled"
          onClose={() => setFeedback((current) => ({ ...current, open: false }))}
          sx={{ width: '100%' }}
        >
          {feedback.message}
        </Alert>
      </Snackbar>
    </ReactFlowProvider>
  );
};

export default WorkflowsPage;








